import { FormationItemData } from '../../../shared/dto/EntityRepository';
import { DisplaySize, FormationType } from '../../../shared/enum/EnumRepository';
import {
  getFormationVisibleImageBottom,
  getFormationVisibleImageRight,
  NormalizedFormationType,
  normalizeFormationDisplaySize,
  normalizeFormationType,
} from '../../../shared/util/formationUtils';

export const FORMATION_DISPLAY_SIZE_OPTIONS = [
  DisplaySize.WIDE,
  DisplaySize.BIG,
  DisplaySize.MEDIUM,
] as const;

export const FORMATION_TYPE_OPTIONS = [
  FormationType.TWO_COLUMNS,
  FormationType.FEATURE_SIDE,
  FormationType.THREE_COLUMNS,
] as const;

/** 화면 폭 대비 Formation 콘텐츠 폭. 내부 비율과 간격 규칙은 유지한다. */
const FORMATION_CONTENT_WIDTH_RATIO = {
  [DisplaySize.WIDE]: 1,
  [DisplaySize.BIG]: 0.9,
  [DisplaySize.MEDIUM]: 0.8,
} as const;

const FORMATION_MOBILE_CONTENT_WIDTH_RATIO = {
  [DisplaySize.WIDE]: 1,
  [DisplaySize.BIG]: 0.9,
  [DisplaySize.MEDIUM]: 0.9,
} as const;

const FORMATION_PDF_WIDE_CONTENT_WIDTH_RATIO = 0.94;

/** 현재 FormationType별 전체 그리드 비율. */
const FORMATION_GRID_ASPECT_RATIO: Record<
  NormalizedFormationType,
  { width: number; height: number }
> = {
  [FormationType.TWO_COLUMNS]: { width: 100, height: 60 },
  [FormationType.FEATURE_SIDE]: { width: 100, height: 60 },
  [FormationType.THREE_COLUMNS]: { width: 100, height: 45 },
};

/** Formation 너비 대비 슬롯 사이 간격(%). */
export const FORMATION_GRID_GAP_PERCENT = 0.8;
export const FORMATION_INFO_GAP = 8;
export const FORMATION_INFO_ROW_GAP = 2;

export function getFormationContentWidthRatio(
  displaySize: DisplaySize,
  isMobile = false,
): number {
  const normalized = normalizeFormationDisplaySize(displaySize);
  const ratioMap = isMobile
    ? FORMATION_MOBILE_CONTENT_WIDTH_RATIO
    : FORMATION_CONTENT_WIDTH_RATIO;
  return ratioMap[normalized as keyof typeof ratioMap];
}

export function getFormationGridAspectRatio(formationType: FormationType): {
  width: number;
  height: number;
} {
  const normalizedType = normalizeFormationType(formationType);
  return FORMATION_GRID_ASPECT_RATIO[normalizedType]
    ?? FORMATION_GRID_ASPECT_RATIO[FormationType.TWO_COLUMNS];
}

export function getFormationSlotAspectRatio(
  formationType: FormationType,
  slotIndex: number,
): { width: number; height: number } {
  const normalizedType = normalizeFormationType(formationType);
  const gridRatio = getFormationGridAspectRatio(normalizedType);

  switch (normalizedType) {
    case FormationType.TWO_COLUMNS:
      return {
        width: (gridRatio.width - FORMATION_GRID_GAP_PERCENT) / 2,
        height: gridRatio.height,
      };
    case FormationType.FEATURE_SIDE:
      return {
        width: (gridRatio.width - FORMATION_GRID_GAP_PERCENT)
          * (slotIndex === 0 ? 3 / 5 : 2 / 5),
        height: gridRatio.height,
      };
    case FormationType.THREE_COLUMNS:
      return {
        width: (gridRatio.width - FORMATION_GRID_GAP_PERCENT * 2) / 3,
        height: gridRatio.height,
      };
    default:
      return gridRatio;
  }
}

export interface FormationSlotPlacement {
  gridColumn: string;
  gridRow: string;
}

export interface FormationGridStyle {
  gridTemplateColumns: string;
  gridTemplateRows: string;
}

const FEATURE_SIDE_GRID: FormationGridStyle = {
  gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
  gridTemplateRows: '1fr',
};

export function getFormationGridStyle(formationType: FormationType): FormationGridStyle {
  switch (normalizeFormationType(formationType)) {
    case FormationType.TWO_COLUMNS:
      return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
    case FormationType.FEATURE_SIDE:
      return FEATURE_SIDE_GRID;
    case FormationType.THREE_COLUMNS:
      return {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridTemplateRows: '1fr',
      };
    default:
      return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
  }
}

export interface FormationPdfSlotDimensions {
  width: number;
  height: number;
}

export interface FormationPdfLayout {
  shellPaddingHorizontal: number;
  gridWidth: number;
  gridHeight: number;
  gap: number;
  slots: FormationPdfSlotDimensions[];
}

export interface FormationPdfInfoPosition {
  marginTop: number;
  paddingRight: number;
}

/** WIDE/BIG/MEDIUM displaySize에 따른 PDF 그리드 치수 */
export function getFormationPdfLayout(
  formationType: FormationType,
  contentWidth: number,
  displaySize: DisplaySize,
): FormationPdfLayout {
  const normalizedType = normalizeFormationType(formationType);
  const isWide = displaySize === DisplaySize.WIDE;
  const widthRatio = isWide
    ? FORMATION_PDF_WIDE_CONTENT_WIDTH_RATIO
    : getFormationContentWidthRatio(displaySize);
  const shellPaddingHorizontal = isWide
    ? contentWidth * ((1 - FORMATION_PDF_WIDE_CONTENT_WIDTH_RATIO) / 2)
    : contentWidth * ((1 - widthRatio) / 2);
  const gridWidth = contentWidth * widthRatio;
  const aspectRatio = getFormationGridAspectRatio(normalizedType);
  const gridHeight =
    gridWidth * (aspectRatio.height / aspectRatio.width);
  const gap = 8;

  switch (normalizedType) {
    case FormationType.TWO_COLUMNS: {
      const innerWidth = gridWidth - gap;
      const slotWidth = innerWidth / 2;
      return {
        shellPaddingHorizontal,
        gridWidth,
        gridHeight,
        gap,
        slots: [
          { width: slotWidth, height: gridHeight },
          { width: slotWidth, height: gridHeight },
        ],
      };
    }
    case FormationType.FEATURE_SIDE: {
      const innerWidth = gridWidth - gap;
      const leftWidth = innerWidth * (3 / 5);
      const rightWidth = innerWidth * (2 / 5);
      return {
        shellPaddingHorizontal,
        gridWidth,
        gridHeight,
        gap,
        slots: [
          { width: leftWidth, height: gridHeight },
          { width: rightWidth, height: gridHeight },
        ],
      };
    }
    case FormationType.THREE_COLUMNS: {
      const slotWidth = (gridWidth - gap * 2) / 3;
      return {
        shellPaddingHorizontal,
        gridWidth,
        gridHeight,
        gap,
        slots: [
          { width: slotWidth, height: gridHeight },
          { width: slotWidth, height: gridHeight },
          { width: slotWidth, height: gridHeight },
        ],
      };
    }
    default:
      return {
        shellPaddingHorizontal: 0,
        gridWidth,
        gridHeight: gridWidth,
        gap: 0,
        slots: [{ width: gridWidth, height: gridHeight }],
      };
  }
}

export function getFormationPdfInfoPosition(
  items: FormationItemData[],
  layout: FormationPdfLayout,
  contentWidth: number,
  displaySize: DisplaySize,
): FormationPdfInfoPosition {
  const sortedItems = [...items].sort((a, b) => a.slotIndex - b.slotIndex);
  let slotLeft = 0;
  let lowestImageBottom: number | null = null;
  let rightmostImageRight: number | null = null;

  sortedItems.forEach((item, index) => {
    const dimensions = layout.slots[index] ?? layout.slots[layout.slots.length - 1];
    if (!dimensions) return;

    const imageBottom = getFormationVisibleImageBottom(
      item,
      0,
      dimensions.width,
      dimensions.height,
    );
    if (imageBottom !== null) {
      lowestImageBottom = lowestImageBottom === null
        ? imageBottom
        : Math.max(lowestImageBottom, imageBottom);
    }

    const imageRight = getFormationVisibleImageRight(
      item,
      slotLeft,
      dimensions.width,
      dimensions.height,
    );
    if (imageRight !== null) {
      rightmostImageRight = rightmostImageRight === null
        ? imageRight
        : Math.max(rightmostImageRight, imageRight);
    }

    slotLeft += dimensions.width + layout.gap;
  });

  const normalizedDisplaySize = normalizeFormationDisplaySize(displaySize);
  const maximumInfoRight = normalizedDisplaySize === DisplaySize.WIDE
    ? contentWidth * (
        (1 + getFormationContentWidthRatio(DisplaySize.BIG)) / 2
      ) - layout.shellPaddingHorizontal
    : layout.gridWidth;
  const infoRight = rightmostImageRight === null
    ? maximumInfoRight
    : Math.min(maximumInfoRight, rightmostImageRight);

  return {
    marginTop: FORMATION_INFO_GAP - (
      layout.gridHeight - (lowestImageBottom ?? layout.gridHeight)
    ),
    paddingRight: Math.max(0, layout.gridWidth - infoRight),
  };
}

export function getFormationSlotPlacement(
  slotIndex: number,
  formationType: FormationType,
): FormationSlotPlacement {
  switch (normalizeFormationType(formationType)) {
    case FormationType.TWO_COLUMNS:
      return {
        gridColumn: String(slotIndex + 1),
        gridRow: '1',
      };
    case FormationType.FEATURE_SIDE:
      return {
        gridColumn: slotIndex === 0 ? '1' : '2',
        gridRow: '1',
      };
    case FormationType.THREE_COLUMNS:
      return {
        gridColumn: String(slotIndex + 1),
        gridRow: '1',
      };
    default:
      return { gridColumn: '1', gridRow: '1' };
  }
}
